import type { IssueAttachmentModel } from '@supply-mover-2-0/shared';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { ImageViewerModal } from '../ImageViewerModal/ImageViewerModal';

interface IssueImageThumbnailsProps {
	attachments: Array<IssueAttachmentModel>;
	/**
	 * Resolves a signed URL for an image the payload did not carry one for.
	 * The CRM passes the file endpoint lookup; the storefront's payloads are
	 * already signed.
	 */
	resolveUrl?: (fileId: number) => Promise<string | null>;
	/** Thumbnail edge in pixels. */
	size?: number;
	className?: string;
}

/**
 * PD-16475 — the images sent with an issue message, as a row of thumbnails.
 * Clicking one opens the shared image viewer on it, with the others a swipe
 * away.
 */
export function IssueImageThumbnails({
	attachments,
	resolveUrl,
	size = 72,
	className,
}: IssueImageThumbnailsProps): React.JSX.Element | null {
	const { t } = useTranslation();
	const [urls, setUrls] = useState<Record<number, string | null>>({});
	const [viewerIndex, setViewerIndex] = useState<number | null>(null);

	const attachmentKey = attachments
		.map((a) => `${a.fileId}:${a.url ?? ''}`)
		.join(',');

	useEffect(() => {
		let cancelled = false;

		const load = async (): Promise<void> => {
			const entries = await Promise.all(
				attachments.map(
					async (attachment): Promise<[number, string | null]> => {
						if (attachment.url != null && attachment.url.length > 0) {
							return [attachment.fileId, attachment.url];
						}

						if (resolveUrl == null) {
							return [attachment.fileId, null];
						}

						try {
							return [attachment.fileId, await resolveUrl(attachment.fileId)];
						} catch (_err) {
							return [attachment.fileId, null];
						}
					},
				),
			);

			if (!cancelled) {
				setUrls(Object.fromEntries(entries));
			}
		};

		void load();

		return (): void => {
			cancelled = true;
		};
		// `attachmentKey` captures the attachments; resolveUrl is a stable lookup.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [attachmentKey]);

	if (attachments.length === 0) {
		return null;
	}

	const viewable = attachments
		.map((attachment) => ({
			url: urls[attachment.fileId] ?? null,
			title: attachment.name,
		}))
		.filter(
			(image): image is { url: string; title: string } => image.url != null,
		);

	return (
		<>
			<div className={cn('flex flex-wrap gap-2', className)}>
				{attachments.map((attachment) => {
					const url = urls[attachment.fileId] ?? null;
					const index = viewable.findIndex((image) => image.url === url);

					return (
						<button
							key={attachment.fileId}
							type="button"
							className="overflow-hidden rounded-md border border-default bg-sunken focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
							style={{ width: size, height: size }}
							onClick={() => {
								if (index >= 0) {
									setViewerIndex(index);
								}
							}}
							aria-label={t('View image {{name}}', { name: attachment.name })}
						>
							{url != null ? (
								<img
									src={url}
									alt={t(attachment.name)}
									className="h-full w-full object-cover"
									loading="lazy"
								/>
							) : (
								<span className="flex h-full w-full items-center justify-center text-xs text-muted">
									{t('Image')}
								</span>
							)}
						</button>
					);
				})}
			</div>

			<ImageViewerModal
				isOpen={viewerIndex != null}
				onClose={() => {
					setViewerIndex(null);
				}}
				images={viewable}
				initialIndex={viewerIndex ?? 0}
				hideThumbnails={viewable.length <= 1}
			/>
		</>
	);
}
